import { useEffect, useRef, useState, useCallback } from 'react';

/** Thirty minutes. The recorder stops itself there and the note is still sent — the old
 * two-minute cap threw the recording away at the mark, which read as a glitch. */
export const MAX_VOICE_SECONDS = 30 * 60;

/**
 * Recording a voice note.
 *
 * Tap to start, tap to stop — not hold-to-talk. Holding is fine for a two-second
 * "on my way" and miserable for anything longer, and it can't survive the phone
 * leaving your ear or a scroll gesture stealing the touch. Tap/tap also gives an
 * obvious place to put CANCEL, which hold-to-talk has to invent a slide gesture
 * for.
 *
 * The stream is stopped on every exit path. A MediaRecorder that is torn down
 * without releasing its tracks leaves the OS microphone indicator lit, which
 * reads as an app still listening to you.
 */
export function useVoiceRecorder({ onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const resolveRef = useRef(null);
  // A recording the cap stopped on its own, waiting for SEND.
  const keptRef = useRef(null);

  const release = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // Unmounting mid-recording (navigating away) must also drop the mic.
  useEffect(() => release, [release]);

  const start = useCallback(async () => {
    if (recording) return false;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      onError?.('Voice notes need a browser with microphone recording.');
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Let the browser choose: Chrome gives webm/opus, Safari mp4/aac, and each
      // plays back what it produced. Naming a mimeType it doesn't support throws.
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        release();
        // Stopped by the cap rather than by stop(): keep it for the SEND that follows.
        if (resolveRef.current) { resolveRef.current(blob); resolveRef.current = null; } else keptRef.current = blob;
      };
      keptRef.current = null;
      rec.start();
      recorderRef.current = rec;
      setSeconds(0);
      setRecording(true);
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          // At the cap the recorder stops but the row stays up with its SEND button; the
          // blob waits in keptRef for it.
          if (s + 1 >= MAX_VOICE_SECONDS) {
            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            try { if (rec.state !== 'inactive') rec.stop(); } catch { /* already stopped */ }
          }
          return Math.min(s + 1, MAX_VOICE_SECONDS);
        });
      }, 1000);
      return true;
    } catch (e) {
      release();
      onError?.(e?.name === 'NotAllowedError'
        ? 'Microphone permission denied.'
        : 'Could not start recording.');
      return false;
    }
  }, [recording, onError, release]);

  /** Stop and hand back the recorded blob (null if nothing usable). */
  const stop = useCallback(() => new Promise((resolve) => {
    const rec = recorderRef.current;
    const kept = keptRef.current;
    if (kept) { keptRef.current = null; release(); setRecording(false); resolve(kept); return; }
    if (!rec || rec.state === 'inactive') { release(); setRecording(false); resolve(null); return; }
    resolveRef.current = resolve;
    setRecording(false);
    try { rec.stop(); } catch { release(); resolve(null); }
  }), [release]);

  /** Throw the recording away — the mic still has to be released. */
  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    resolveRef.current = null;
    keptRef.current = null;
    setRecording(false);
    setSeconds(0);
    if (rec && rec.state !== 'inactive') { rec.onstop = null; try { rec.stop(); } catch { /* ignore */ } }
    release();
  }, [release]);

  return { recording, seconds, start, stop, cancel };
}

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
